import { Link } from "react-router-dom";
import { useAdvanceWizardStep } from "../../hooks/use-wizard.js";
import { useFirstOrg } from "../../hooks/use-orgs.js";
import { useWebsites } from "../../hooks/use-websites.js";
import { Button } from "../../components/button.js";
import { StatusBadge } from "../../components/status-badge.js";
import type {
  WizardState,
  ScanDepth,
  ScanFrequency,
  WcagLevel,
  OrgType,
} from "../../api/types.js";

const DEPTH_LABELS: Record<ScanDepth, string> = {
  "10": "10 pages",
  "20": "20 pages",
  "50": "50 pages",
  "100": "100 pages",
  "150": "150 pages",
  "250": "250 pages",
  "500": "500 pages",
  "1000": "1,000 pages",
};

const FREQ_LABELS: Record<ScanFrequency, string> = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  manual: "Manual only",
};

const WCAG_LABELS: Record<WcagLevel, string> = {
  WCAG21AA: "WCAG 2.1 AA",
  WCAG22AA: "WCAG 2.2 AA",
};

const VERIFY_LABELS: Record<string, string> = {
  dns_txt: "DNS TXT record",
  html_meta: "HTML meta tag",
  file_upload: "Verification file",
};

const ORG_TYPE_LABELS: Record<OrgType, string> = {
  public_body: "Public body / government",
  private_company: "Private company",
  non_profit: "Non-profit / charity",
  educational: "Educational institution",
  healthcare: "Healthcare provider",
  other: "Other",
};

const PLAN_LABELS: Record<string, string> = {
  free: "Free",
  starter: "Starter",
  growth: "Growth",
  pro: "Pro",
  enterprise: "Enterprise",
};

interface ReviewStepProps {
  websiteId: string;
  wizardState: WizardState;
}

export function ReviewStep({ websiteId, wizardState }: ReviewStepProps) {
  const org = useFirstOrg();
  const { data: websitesData } = useWebsites(org?.id);
  const advance = useAdvanceWizardStep(websiteId);

  const websiteUrl = websitesData?.websites.find((w) => w.id === websiteId)?.url;

  // Pull data from completed steps
  const planStepData = wizardState.steps.find(
    (s) => s.step === "plan_selection",
  )?.data;
  const depthData = wizardState.steps.find(
    (s) => s.step === "scan_depth",
  )?.data;
  const freqData = wizardState.steps.find(
    (s) => s.step === "scan_frequency",
  )?.data;
  const wcagData = wizardState.steps.find(
    (s) => s.step === "wcag_standard",
  )?.data;
  const stmtData = wizardState.steps.find(
    (s) => s.step === "statement_setup",
  )?.data;
  const verData = wizardState.steps.find(
    (s) => s.step === "protected_site",
  )?.data;
  const intakeData = wizardState.steps.find(
    (s) => s.step === "discovery_preview",
  )?.data;

  const scanDepth = (depthData?.depth as ScanDepth | undefined) ?? "20";
  const scanFreq =
    (freqData?.frequency as ScanFrequency | undefined) ?? "monthly";
  const wcagLevel =
    (wcagData?.wcagLevel as WcagLevel | undefined) ?? "WCAG21AA";
  const selectedPlanId = (planStepData?.planId as string | undefined) ?? "free";
  const selectedPlanLabel = PLAN_LABELS[selectedPlanId] ?? selectedPlanId;
  const orgName = (stmtData?.orgName as string | undefined) ?? "—";
  const contactEmail =
    (stmtData?.accessibilityContactEmail as string | undefined) ?? "—";
  const orgType =
    (stmtData?.orgType as OrgType | undefined) ?? "private_company";
  const verMethod = (verData?.method as string | undefined) ?? null;
  const isVerified = verData?.verified === true;
  const intakeWarnCount = (intakeData?.warningCount as number | undefined) ?? 0;
  const intakeAdvCount = (intakeData?.advisoryCount as number | undefined) ?? 0;

  function handleFinish() {
    advance.mutate({ step: "review_launch", outcome: "pass" });
  }

  const isCompleted = wizardState.completed;

  return (
    <div className="wizard-content">
      <h2 className="wizard-content__title">Review your setup</h2>
      <p className="wizard-content__subtitle">
        Here is a summary of everything you have configured. Once you finish,
        your site will be ready for its first accessibility scan.
      </p>

      {/* Organisation section */}
      <p className="section-title">Organisation</p>
      <table className="review-table">
        <tbody>
          <tr>
            <td>Name</td>
            <td>{orgName}</td>
          </tr>
          <tr>
            <td>Type</td>
            <td>{ORG_TYPE_LABELS[orgType]}</td>
          </tr>
          <tr>
            <td>Accessibility contact</td>
            <td>{contactEmail}</td>
          </tr>
        </tbody>
      </table>

      {/* Plan & website section */}
      <p className="section-title">Scan setup</p>
      <table className="review-table">
        <tbody>
          <tr>
            <td>Plan</td>
            <td>{selectedPlanLabel}</td>
          </tr>
          <tr>
            <td>URL</td>
            <td style={{ wordBreak: "break-all" }}>
              {websiteUrl ?? "—"}
            </td>
          </tr>
          <tr>
            <td>Scan depth</td>
            <td>{DEPTH_LABELS[scanDepth]}</td>
          </tr>
          <tr>
            <td>Scan frequency</td>
            <td>{FREQ_LABELS[scanFreq]}</td>
          </tr>
          <tr>
            <td>WCAG standard</td>
            <td>{WCAG_LABELS[wcagLevel]}</td>
          </tr>
          <tr>
            <td>Tools used</td>
            <td>Axe-core, PA11y, custom checks in headless Chromium</td>
          </tr>
        </tbody>
      </table>
      <p className="text-muted text-sm" style={{ marginTop: "4px" }}>
        The scanner discovers pages on your site, groups similar pages by
        template, and tests representative pages from each group. You can
        change scan depth and frequency later in Settings.
      </p>

      {/* Verification */}
      <p className="section-title">Verification</p>
      <table className="review-table">
        <tbody>
          <tr>
            <td>Method</td>
            <td>
              {verMethod
                ? (VERIFY_LABELS[verMethod] ?? verMethod)
                : "Not selected"}
            </td>
          </tr>
          <tr>
            <td>Status</td>
            <td>
              {isVerified ? (
                <StatusBadge variant="pass" label="Verified" />
              ) : (
                <StatusBadge variant="advisory" label="Not verified" />
              )}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Intake summary */}
      {(intakeWarnCount > 0 || intakeAdvCount > 0) && (
        <>
          <p className="section-title">Intake findings</p>
          <table className="review-table">
            <tbody>
              {intakeWarnCount > 0 && (
                <tr>
                  <td>Warnings</td>
                  <td>
                    <StatusBadge
                      variant="warning"
                      label={`${intakeWarnCount}`}
                    />
                    <span
                      className="text-sm text-muted"
                      style={{ marginLeft: "0.5rem" }}
                    >
                      Will not block the scan
                    </span>
                  </td>
                </tr>
              )}
              {intakeAdvCount > 0 && (
                <tr>
                  <td>Advisories</td>
                  <td>
                    <StatusBadge
                      variant="advisory"
                      label={`${intakeAdvCount}`}
                    />
                    <span
                      className="text-sm text-muted"
                      style={{ marginLeft: "0.5rem" }}
                    >
                      For your information only
                    </span>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}

      {advance.error && (
        <div className="alert alert--error mt-3">{advance.error.message}</div>
      )}

      {isCompleted && (
        <div className="alert alert--success">
          <p style={{ margin: 0 }}>
            <strong>Setup complete.</strong> Your website is ready for its first
            accessibility scan.
          </p>
        </div>
      )}

      {isCompleted && (
        <div className="wizard-content__actions">
          <Link
            to={`/websites/${websiteId}/scan`}
            className="btn btn--primary"
          >
            Run your first scan
          </Link>
          <Link
            to={`/websites/${websiteId}`}
            className="btn btn--secondary"
          >
            Go to dashboard
          </Link>
        </div>
      )}

      {!isCompleted && (
        <div className="wizard-content__actions">
          <Button onClick={handleFinish} loading={advance.isPending}>
            Finish setup
          </Button>
        </div>
      )}
    </div>
  );
}
